/**
 * Settings Model for V2 Services
 */

export interface AppSettings {
  appName: string;
  version: string;
  maintenance: boolean;
  maintenanceMessage?: string;
  defaultCurrency: string;
  supportedCurrencies: string[];
  timeZone: string;
  language: string;
  supportedLanguages: string[];
}

export interface BookingSettings {
  maxAdvanceBookingDays: number;
  minAdvanceBookingHours: number;
  defaultBookingDuration: number;
  allowSameDayBooking: boolean;
  requirePaymentUpfront: boolean;
  cancellationWindowHours: number;
  refundPolicy: string;
  autoConfirmBookings: boolean;
}

export interface PricingSettings {
  basePrice: number;
  pricePerSquareFoot: number;
  minimumCharge: number;
  rushHourMultiplier: number;
  weekendMultiplier: number;
  holidayMultiplier: number;
  discountThresholds: Record<string, number>;
  taxRate: number;
}

export interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  bookingReminders: boolean;
  reminderHours: number[];
  marketingEmails: boolean;
  adminAlerts: boolean;
}

export interface RobotSettings {
  maxRobotsPerBuilding: number;
  batteryThreshold: number;
  maintenanceInterval: number;
  autoAssignment: boolean;
  defaultCleaningMode: string;
  availableCleaningModes: string[];
}

export interface SettingsData {
  id?: string;
  app: AppSettings;
  booking: BookingSettings;
  pricing: PricingSettings;
  notifications: NotificationSettings;
  robots: RobotSettings;
  lastUpdatedBy?: string;
  lastUpdatedAt: Date;
  createdAt: Date;
}

export class Settings {
  public id?: string;
  public app: AppSettings;
  public booking: BookingSettings;
  public pricing: PricingSettings;
  public notifications: NotificationSettings;
  public robots: RobotSettings;
  public lastUpdatedBy?: string;
  public lastUpdatedAt: Date;
  public createdAt: Date;

  constructor(data?: Partial<SettingsData>) {
    this.id = data?.id;
    this.app = data?.app || this.getDefaultAppSettings();
    this.booking = data?.booking || this.getDefaultBookingSettings();
    this.pricing = data?.pricing || this.getDefaultPricingSettings();
    this.notifications = data?.notifications || this.getDefaultNotificationSettings();
    this.robots = data?.robots || this.getDefaultRobotSettings();
    this.lastUpdatedBy = data?.lastUpdatedBy;
    this.lastUpdatedAt = data?.lastUpdatedAt || new Date();
    this.createdAt = data?.createdAt || new Date();
  }

  private getDefaultAppSettings(): AppSettings {
    return {
      appName: 'HappyTidy',
      version: '2.0.0',
      maintenance: false,
      defaultCurrency: 'USD',
      supportedCurrencies: ['USD', 'EUR', 'GBP'],
      timeZone: 'America/New_York',
      language: 'en',
      supportedLanguages: ['en', 'es', 'fr']
    };
  }

  private getDefaultBookingSettings(): BookingSettings {
    return {
      maxAdvanceBookingDays: 30,
      minAdvanceBookingHours: 2,
      defaultBookingDuration: 120,
      allowSameDayBooking: true,
      requirePaymentUpfront: true,
      cancellationWindowHours: 24,
      refundPolicy: 'full_refund_24h',
      autoConfirmBookings: false
    };
  }

  private getDefaultPricingSettings(): PricingSettings {
    return {
      basePrice: 50,
      pricePerSquareFoot: 0.1,
      minimumCharge: 30,
      rushHourMultiplier: 1.2,
      weekendMultiplier: 1.1,
      holidayMultiplier: 1.5,
      discountThresholds: {
        '5_bookings': 0.05,
        '10_bookings': 0.1,
        '20_bookings': 0.15
      },
      taxRate: 0.08
    };
  }

  private getDefaultNotificationSettings(): NotificationSettings {
    return {
      emailNotifications: true,
      smsNotifications: true,
      pushNotifications: true,
      bookingReminders: true,
      reminderHours: [24, 2],
      marketingEmails: false,
      adminAlerts: true
    };
  }

  private getDefaultRobotSettings(): RobotSettings {
    return {
      maxRobotsPerBuilding: 5,
      batteryThreshold: 20,
      maintenanceInterval: 30,
      autoAssignment: true,
      defaultCleaningMode: 'standard',
      availableCleaningModes: ['eco', 'standard', 'deep', 'quick']
    };
  }

  public toFirestore(): Record<string, any> {
    return {
      app: this.app,
      booking: this.booking,
      pricing: this.pricing,
      notifications: this.notifications,
      robots: this.robots,
      lastUpdatedBy: this.lastUpdatedBy,
      lastUpdatedAt: this.lastUpdatedAt,
      createdAt: this.createdAt
    };
  }

  public static fromFirestore(data: any): Settings {
    return new Settings({
      ...data,
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      lastUpdatedAt: data.lastUpdatedAt?.toDate?.() || data.lastUpdatedAt
    });
  }

  public updateAppSettings(updates: Partial<AppSettings>, updatedBy: string): void {
    this.app = { ...this.app, ...updates };
    this.lastUpdatedBy = updatedBy;
    this.lastUpdatedAt = new Date();
  }

  public updateBookingSettings(updates: Partial<BookingSettings>, updatedBy: string): void {
    this.booking = { ...this.booking, ...updates };
    this.lastUpdatedBy = updatedBy;
    this.lastUpdatedAt = new Date();
  }

  public updatePricingSettings(updates: Partial<PricingSettings>, updatedBy: string): void {
    this.pricing = { ...this.pricing, ...updates };
    this.lastUpdatedBy = updatedBy;
    this.lastUpdatedAt = new Date();
  }

  public updateNotificationSettings(updates: Partial<NotificationSettings>, updatedBy: string): void {
    this.notifications = { ...this.notifications, ...updates };
    this.lastUpdatedBy = updatedBy;
    this.lastUpdatedAt = new Date();
  }

  public updateRobotSettings(updates: Partial<RobotSettings>, updatedBy: string): void {
    this.robots = { ...this.robots, ...updates };
    this.lastUpdatedBy = updatedBy;
    this.lastUpdatedAt = new Date();
  }

  public enableMaintenance(message?: string, updatedBy?: string): void {
    this.app.maintenance = true;
    this.app.maintenanceMessage = message;
    this.lastUpdatedBy = updatedBy;
    this.lastUpdatedAt = new Date();
  }

  public disableMaintenance(updatedBy?: string): void {
    this.app.maintenance = false;
    this.app.maintenanceMessage = undefined;
    this.lastUpdatedBy = updatedBy;
    this.lastUpdatedAt = new Date();
  }
}